import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  EventEmitter,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { StateService } from '../../services/state.service';
import { Subscription } from 'rxjs';
import { Field } from '../../interfaces/field';
import { ActivatedRoute } from '@angular/router';
import { Product } from '../../interfaces/product';
import { NgxSmartModalService } from 'ngx-smart-modal';

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SummaryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('lastFields') lastFields: ElementRef;
  @ViewChild('mobileFieldsContainer', { static: true }) mobileFieldsContainer: ElementRef;
  @ViewChild('detailsContainer') detailsContainer: ElementRef;
  @ViewChild('detailsInner') detailsInner: ElementRef;
  @Input() index: number;
  @Input() selectedIndex: number;
  @Input() form: any = {config: {parts: []}};
  @Input() totalVat: number;
  @Input() totalPrice: number;
  @Input() singlePrice: number;
  @Input() returnPrice: number;
  @Input() transactionCosts: number;
  @Input() formGroup: UntypedFormGroup;
  @Input() formNavigation: ElementRef;
  @Input() layout = 'page-layout';
  @Input() singleProducts!: Product[];
  @Input() returnProducts!: Product[];
  @Input() vatPercentage!: number;
  @Input() hideVat = false;
  @Input() buttonstyle: string = 'rounded';
  @Input() activeTab: string = 'route';

  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() bookOrder: EventEmitter<any> = new EventEmitter<any>();
  @Output() navigate: EventEmitter<any> = new EventEmitter<any>();

  parts: any[] = [];
  orderMode: string;
  orderModeSub: Subscription;
  airportDeparture: boolean;
  airportDepartureSub: Subscription;
  airportDestination: boolean;
  airportDestinationSub: Subscription;
  returnDepartureField: Field;
  returnDestinationField: Field;
  faChevronDown = 'chevron-down';
  faChevronUp = 'chevron-up';
  detailsOpen = false;
  // True only when the collapsed details actually overflow their clipped height.
  // When the details already fit (e.g. the price card is taller), the fade + the
  // open/close-details toggle are pointless, so they are hidden.
  detailsOverflowing = false;
  private detailsResizeObserver?: ResizeObserver;
  currency: string;
  formSubscription: Subscription;
  cdnUrl = '';
  _state: StateService;
  summaryDialogId = `bookingSummaryDialog_${Math.random().toString(36).substr(2, 9)}`;

  constructor(protected state: StateService,
              private route: ActivatedRoute,
              private zone: NgZone,
              public modalService: NgxSmartModalService) {

    if (!this.state.formConfig) {
      this.formSubscription = this.route.data.subscribe((data: {form: any}) => {
        if(data.form) {
          this.cdnUrl = data.form.config.cdnUrl ? data.form.config.cdnUrl : '';
          }
      });
    } else {
      this.cdnUrl = this.state.formConfig.cdnUrl;
    }
    this._state = state;
  }

  ngOnInit() {
    this.currency = this.state.currency;
    this.formGroup = this.state.formGroup;
    this.parts = this.state.formParts;
    this.returnDepartureField = this.state.returnDepartureField;
    this.returnDestinationField = this.state.returnDestinationField;

    this.orderModeSub = this.state.orderMode$.subscribe(
      orderMode => this.orderMode = orderMode
    );
    this.airportDepartureSub = this.state.airportDeparture$.subscribe(
      airportDeparture => this.airportDeparture = airportDeparture
    );
    this.airportDestinationSub = this.state.airportDestination$.subscribe(
      airportDestination => this.airportDestination = airportDestination
    );
  }

  get hasCollapsibleContent(): boolean {
    return (this.parts[this.index]?.summary || []).some(partIndex =>
      this.parts[partIndex]?.type !== 'summary' && this.parts[partIndex]?.type !== 'products'
    );
  }

  ngAfterViewInit(): void {
    // Decide whether the details actually need a toggle. The right column stretches
    // to the left column's height, so the details only overflow when they're taller
    // than the price card. Re-measure on layout changes (ResizeObserver).
    const container = this.detailsContainer?.nativeElement;
    const inner = this.detailsInner?.nativeElement;
    if (!container || !inner) { return; }
    // Defer the first measure so the flex layout has settled.
    setTimeout(() => this.measureDetailsOverflow(), 0);
    if (typeof ResizeObserver !== 'undefined') {
      this.detailsResizeObserver = new ResizeObserver(() => {
        // ResizeObserver runs outside Angular's zone; re-enter so the *ngIf updates.
        this.zone.run(() => this.measureDetailsOverflow());
      });
      this.detailsResizeObserver.observe(container);
      this.detailsResizeObserver.observe(inner);
    }
  }

  private measureDetailsOverflow(): void {
    const inner = this.detailsInner?.nativeElement;
    if (!inner) { return; }
    // While expanded the container grows to full height (no clip), so overflow can't
    // be measured — keep the toggle so the user can collapse again. Only (re)evaluate
    // while collapsed.
    if (this.detailsOpen) { return; }
    this.detailsOverflowing = (inner.scrollHeight - inner.clientHeight) > 1;
  }

  // Parts that have any field with a product surcharge
  private hasProductField(partIndex: number): boolean {
    return (this.parts[partIndex]?.fields || []).some((f: any) => f.product);
  }

  // Parts whose fields are exclusively personal contact info (text/email/phone/tel)
  // with no product surcharges — these go last
  private isPersonalInfoPart(partIndex: number): boolean {
    const fields: any[] = this.parts[partIndex]?.fields || [];
    return fields.length > 0 &&
      !fields.some((f: any) => f.product) &&
      fields.some((f: any) => ['text', 'email', 'phone', 'tel'].includes(f.type));
  }

  // Summary part indices reordered: product-field parts before personal-info parts
  get orderedSummaryIndices(): number[] {
    return [...(this.parts[this.index]?.summary || [])].sort((a, b) => {
      const aPersonal = this.isPersonalInfoPart(a);
      const bPersonal = this.isPersonalInfoPart(b);
      if (aPersonal && !bPersonal) return 1;
      if (!aPersonal && bPersonal) return -1;
      return a - b;
    });
  }

  /**
   * Custom fields of the outbound part that also apply to the return leg (they carry a
   * `product` mutation or `meta`/`foreignMeta`, i.e. buildMeta()/calculatePricing() write
   * them onto both jobs) but live only on that part, so the return part's own field list
   * never shows them. Only fields that are active for the return leg and filled are
   * returned; fields the return part already lists are left to that loop.
   */
  returnLegFields(partIndex: number): Field[] {
    const part = this.parts[partIndex];
    if (!part?.fields || !this.formGroup?.controls) {
      return [];
    }
    const returnPart = this.parts[part.returnIndex];
    const values = this.formGroup.getRawValue();
    // The return part's own loop renders its nested children too, so they count as listed.
    const listed = new Set<string>();
    (returnPart?.fields || []).forEach((f: any) => {
      listed.add(f.property);
      (f.fields || []).forEach((n: Field) => listed.add(n.property));
    });
    const flat: Field[] = [];
    part.fields.forEach((f: any) => {
      flat.push(f);
      // A nested child only counts when its parent is itself active on the return leg.
      if (this.isActiveOnReturnLeg(f, values)) {
        (f.fields || []).forEach((n: Field) => flat.push(n));
      }
    });
    return flat.filter((f: any) => {
      if (!f.property || f.type === 'collapse' || listed.has(f.property)) {
        return false;
      }
      if (!(f.product || f.meta || f.foreignMeta)) {
        return false;
      }
      const value = values[f.property];
      if (value === undefined || value === null || value === '' || value === 0 || value === false) {
        return false;
      }
      return this.isActiveOnReturnLeg(f, values);
    });
  }

  // Return-leg twin of OrderComponent.isConditionalFieldActive(field, values, 'return').
  private isActiveOnReturnLeg(field: any, values: any): boolean {
    const showIf = field?.showIf;
    if (!showIf) {
      return true;
    }
    return Object.keys(showIf).every((key) => {
      const expected = showIf[key];
      if (key === 'addressAirport') {
        if (expected === 'any') {
          return !!(this.airportDeparture || this.airportDestination);
        }
        // On the return job departure/destination swap, and it only exists with a return booked.
        if (expected === 'departure') {
          return !!values?.returnTrip && !!this.airportDestination;
        }
        if (expected === 'destination') {
          return !!values?.returnTrip && !!this.airportDeparture;
        }
        return false;
      }
      if (key === 'orderMode') {
        return !this.orderMode || this.orderMode === expected;
      }
      if (key === 'product' && Array.isArray(expected)) {
        const id = values?.product?.productId;
        return !!id && expected.includes(id);
      }
      if (key === 'productSku' && expected) {
        const skus = (Array.isArray(expected) ? expected : [expected]).map((s) => String(s).toLowerCase());
        const sku = values?.product?.sku;
        return !!sku && skus.includes(String(sku).toLowerCase());
      }
      return values?.[key] === expected;
    });
  }

  // Within a part, put fields with a product before the passengers/number field
  sortedPartFields(fields: any[]): any[] {
    if (!fields) return [];
    const withProduct = fields.filter(f => f.product);
    const without = fields.filter(f => !f.product);
    // Split without-product into "before passengers" and passengers/extras
    const passengersIdx = without.findIndex(f => f.property === 'passengers' || f.type === 'passengers');
    if (passengersIdx === -1 || withProduct.length === 0) return fields;
    return [
      ...without.slice(0, passengersIdx),
      ...withProduct,
      ...without.slice(passengersIdx),
    ];
  }

  ngOnDestroy(): void {
    if (this.detailsResizeObserver) {
      this.detailsResizeObserver.disconnect();
    }
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.orderModeSub) {
      this.orderModeSub.unsubscribe();
    }
    if (this.airportDepartureSub) {
      this.airportDepartureSub.unsubscribe();
    }
    if (this.airportDestinationSub) {
      this.airportDestinationSub.unsubscribe();
    }
  }

  navigateForm(direction) {
    this.navigate.emit(direction);
    this.selectedIndex = this.selectedIndex -1;
  }

  bookOrderForm() {
    const self = this;
    this.bookOrder.emit();
  }

  toggleHelpModal($event) {
    this.openHelpModal.emit($event);
  }

  openSummaryDialog() {
    this.modalService.getModal(this.summaryDialogId).open();
  }

  get isMetered(): boolean {
    return !this.formGroup?.controls['product']?.value?.price?.total;
  }

  /**
   * Surcharge rows for the price card: one row per field. On a return trip the outbound and
   * return line items of the same field are merged into a single row carrying their sum, so a
   * surcharge that is billed per leg shows once (doubled) instead of once per leg.
   * 'global' surcharges already carry `summaryTotal` (both legs) and are the same object in
   * both lists, so their return copy is skipped rather than added again.
   */
  get summaryProductRows(): { sku: string; label: string; total: number }[] {
    const isReturn = !!this.formGroup?.controls.returnTrip?.value;
    const rows: { sku: string; label: string; total: number }[] = [];
    const visible = (p: Product) => !!p.amount && !!this.formGroup?.controls[p.sku]?.value;

    (this.singleProducts || []).filter(visible).forEach(p => {
      rows.push({ sku: p.sku, label: p.label, total: p.summaryTotal ?? p.amount.total });
    });
    if (isReturn) {
      (this.returnProducts || []).filter(visible).forEach(p => {
        if (p.summaryTotal !== undefined) {
          return;
        }
        const row = rows.find(r => r.sku === p.sku);
        if (row) {
          row.total += p.amount.total;
        } else {
          rows.push({ sku: p.sku, label: p.label, total: p.amount.total });
        }
      });
    }
    return rows;
  }

  trackBySku(_index: number, row: { sku: string }): string {
    return row.sku;
  }

  get couponDiscountTotal(): number {
    if (!this._state.couponDiscount) return 0;
    return (this._state.couponDiscount.discount ?? 0) + (this._state.couponDiscount.discountRetour ?? 0);
  }

  get subtotal(): number {
    if (!this.hideVat && this.totalVat && this.totalVat > 0) {
      return this.totalPrice - this.totalVat;
    }
    return this.totalPrice;
  }

  isInteger(val: any): boolean {
    return Number.isInteger(val);
  }

}
